import * as Yup from "yup";
import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import {
  Alert,
  Backdrop,
  CircularProgress,
  Container,
  Stack,
} from "@mui/material";
import { useSnackbar } from "../../snackbar";
// import CircularRound from "../../../assets/images/circular lining.svg";
import Iconify from "../../iconify";

function ContactEmailForm() {

  const SERVER_URI = process.env.REACT_APP_SERVER_URI;
  const ISSUES = `${SERVER_URI}/issues`;

  const { enqueueSnackbar } = useSnackbar();
  const [emailData, setEmailData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState("");

  const UpdateEmailSchema = Yup.object().shape({
    firstName: Yup.string()
      .min(1, "First Name must be at least 5 characters")
      .max(50, "First Name must not exceed 50 characters")
      .matches(
        /^[a-zA-Z]+$/,
        "Number, spaces or special characters are not allowed"
      )
      .required("First Name is required"),

    lastName: Yup.string()
      .min(1, "Last Name must be at least 5 characters")
      .max(50, "Last Name must not exceed 50 characters")
      .matches(
        /^[a-zA-Z]+$/,
        "Number, spaces or special characters are not allowed"
      )
      .required("Last Name is required"),

    email: Yup.string()
      .required("Email is required")
      .matches(
        /^[a-zA-Z0-9._+]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        "Email must be a valid email address"
      ),
    mobileNumber: Yup.string()
    .min(10, "Mobile number must be at least 10 digits")
      .matches(
        /^\d{10}$/,
        "Mobile number must be exactly 10 digits and contain only numbers"
      )
      .required("Mobile number is required"),

    file: Yup.mixed().nullable().required("File is required"),
    subject: Yup.string().required("Subject is required"),
    query: Yup.string().required("Query is required"),
  });

  const defaultValues = {
    file: emailData?.file || null,
    firstName: emailData?.firstName || "",
    lastName: emailData?.lastName || "",
    email: emailData?.email || "",
    mobileNumber: emailData?.mobileNumber || "",
    subject: emailData?.subject || "",
    query: emailData?.query || "",
  };

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(UpdateEmailSchema),
    defaultValues,
  });

  const onSubmit = async (data) => {
    setLoading(true);
    setEmailData(data);
    // console.log("Form Data:", data); // Display form data in console
    // enqueueSnackbar(data.firstName, { variant: "error", timer: 10000 });
    try {
      const formData = new FormData();
      Object.keys(data).forEach((key) => {
        if (key === "file") {
          formData.append(key, data[key]); // Append the file to the FormData
        } else {
          formData.append(key, data[key]); // Append other fields
        }
      });

      const response = await axios.post(
        ISSUES,
        formData,
        {
          headers: {
            // Authorization: Bearer ${token},
            "Content-Type": "multipart/form-data",
          },
        }
      ); // Replace with your API endpoint
      // console.log("API Response:", response.data);
      setErrorMessage("");
      enqueueSnackbar(response.data.message, { variant: "success" });
      reset()
    } catch (error) {
      console.error("API Error:", error);
      if (!navigator.onLine) {
        setErrorMessage(
          "You are currently offline. Please check your network connection."
        );
        enqueueSnackbar(error.data.message, { variant: "error" });
      } else {
        setErrorMessage(
          error.message || "An error occurred while processing your request."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const description = {
    typography: {
      fontFamily: "Roboto Flex",
      // fontSize: "22px",
      // fontWeight: 500,
      lineHeight: "27px",
      letterSpacing: "0em",
      // "@media (max-width: 600px)": {
      //   fontSize: "16px",
      //   textAlign: "center",
      // },
    },
  };

  const textStyle = {
    fontFamily: "Roboto Flex",
    fontWeight: 600,
    fontSize: "1rem",
    lineHeight: "25.6px",
    color: "#858585",
  };

  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  });

  return (
    <Container
      maxWidth="lg"
      sx={{
        position: "relative",
        zIndex: 2,
        p: { lg: "40px 86px", md: "30px 50px" ,sm:0,xs:0 },
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)} style={{padding:0}} >
        {loading && (
          <Backdrop
            open={loading}
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: "#fff" }}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        )}

        <Grid container spacing={3} >
          <Grid item xs={12} md={12}>
            {errorMessage && (
              <Alert variant="outlined" severity="error" sx={{ mb: 2 }}>
                {errorMessage}
              </Alert>
            )}
          </Grid>
          <Grid item xs={12} md={12}>
            <Box
              rowGap={4}
              columnGap={3}
              display="grid"
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
              }}
            >
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    // label="First Name"
                    label={ <span style={textStyle}>
                    First Name<span style={{ color: "red" }}> *</span>
                  </span>}
                    variant="outlined"
                    size="medium"
                    fullWidth
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                  />
                )}
              />
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    // label="Last Name"
                    label={<span style={textStyle}>
                    Last Name<span style={{ color: "red" }}> *</span>
                  </span>}
                    variant="outlined"
                    size="medium"
                    fullWidth
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                  />
                )}
              />
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    // label="Email Address"
                    label={ <span style={textStyle}>
                    Email Address<span style={{ color: "red" }}> *</span>
                  </span>}
                    variant="outlined"
                    size="medium"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />
              <Controller
                name="mobileNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    // label="Mobile Number"
                    label={<span style={textStyle}>
                    Mobile Number<span style={{ color: "red" }}> *</span>
                  </span>}
                    variant="outlined"
                    size="medium"
                    fullWidth
                    error={!!errors.mobileNumber}
                    helperText={errors.mobileNumber?.message}
                  />
                )}
              />
            </Box>
            <Box
              rowGap={4}
              columnGap={2}
              display="grid"
              gridTemplateColumns={{
                xs: '1fr',  // Single column for mobile view
                sm: 'repeat(1, 1fr)',
              }}
              sx={{ pt: 4 }}
            >
              <Controller
                name="subject"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    // label="Subject"
                    label={ <span style={textStyle}>
                    Subject<span style={{ color: "red" }}> *</span>
                  </span>}
                    variant="outlined"
                    size="medium"
                    fullWidth
                    error={!!errors.subject}
                    helperText={errors.subject?.message}
                  />
                )}
              />

              <Controller
                name="query"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    rows={4}
                    // label="Describe your query"
                    label={ <span style={textStyle}>
                    Describe your query<span style={{ color: "red" }}> *</span>
                  </span>}
                    variant="outlined"
                    size="medium"
                    fullWidth
                    error={!!errors.query}
                    helperText={errors.query?.message}
                  />
                )}
              />
            </Box>
          </Grid>
        </Grid>

        {/* File Upload */}
        <Box sx={{ mt: 5 }}>
          <Typography sx={{ fontSize: "18px", fontWeight: 600, mb: 1 }}>
          Upload a Document<span style={{ color: "red" }}> *</span>
          </Typography>
          <Controller
            name="file"
            control={control}
            render={({ field }) => (
              <Box>
                <Button
                  component="label"
                  variant="contained"
                  sx={{
                    fontSize: "12px",
                    fontWeight: 500,
                    fontFamily: "Roboto Flex",
                    backgroundColor: "#FE99DD",
                    color: "black",
                    "&:hover": {
                      backgroundColor: "#1E53E8",
                      transform: "scale(1.05)",
                      color: "#C7F760",
                      fontWeight: 600,
                    },
                  }}
                >
                  <Stack>
                    <Iconify icon="mdi-light:file" />
                  </Stack>
                  &nbsp; Choose a file
                  <input
                    type="file"
                    onChange={(e) => {
                      field.onChange(e.target.files[0]); // Set file
                      setFileName(e.target.files[0]?.name || ""); // Update file name
                    }}
                    style={{ display: "none" }}
                  />
                </Button>
                {"   "}
                {!fileName && (
                  <Typography
                    component="span"
                    sx={{
                      ...description.typography,
                      textAlign: "left",
                      marginBlock: "15px",
                      fontSize: { xs: "14px", md: "18px" },
                      fontWeight: 400,
                      textTransform: "capitalize",
                    }}
                  >
                    &nbsp;&nbsp; No file choosen
                  </Typography>
                )}
                <VisuallyHiddenInput type="file" />
                {fileName && (
                  <Typography variant="body2" sx={{ marginTop: 1 }}>
                    Selected file: {fileName}
                  </Typography>
                )}{" "}
                {/* Display selected file name */}
                {errors.file && (
                  <Typography color="error">{errors.file?.message}</Typography>
                )}
              </Box>
            )}
          />
        </Box>

        <Divider sx={{ marginTop: "20px" }} />
        <Typography
          sx={{
            marginTop: "2px",
            fontSize: "14px",
            fontWeight: "600",
            color: "gray",
            opacity: 0.5,
          }}
        >
          You can upload any file in JPEG/PNG/PDF/TXT format with sizes up to
          500 KB.
        </Typography>

        {/* Submit Button */}
        <Box
          sx={{ display: "flex", justifyContent: "center", marginTop: "60px" }}
        >
          <Button
            type="submit"
            variant="contained"
            size="large"
            sx={{
              zIndex: 1,
              width: { xs: "100%", md: "50%" },
              fontSize: "18px",
              fontWeight: 700,
              backgroundColor: "#C7F760",
              color: "#1E53E8",
              textTransform: "capitalize",
              "&:hover": {
                backgroundColor: "#1E53E8",
                transform: "scale(1.05)",
                color: "#C7F760",
                fontWeight: 800,
              },
            }}
            disabled={loading}
          >
            {loading ? "Submitting..." : "Submit"}
          </Button>
        </Box>
      </form>
      {/* 
      <img
        src={CircularRound}
        alt=""
        style={{
          position: "absolute",
          bottom: "0",
          right: "0",
          width: "25%",
          zIndex: "0",
        }}
      /> */}
    </Container>
  );
}

export default ContactEmailForm;
